import * as React from 'react'
import Sockette from 'sockette'

import { useCommittedRef } from '@ulule/owl-kit-components/src/hooks'

type BackersIncrementEvent = {
  event_type: EventType.BACKERS
  data: {
    count: number
  }
}

type DataEvents = {
  status: Status
  events: Event[]
}

export type Event = BackersIncrementEvent | NewsPublishedEvent | OrderEvent | VisitorEvent

export enum EventType {
  'BACKERS' = 'backers_count.incred',
  'NEWS_PUBLISHED' = 'news.published',
  'ORDER' = 'order.completed',
  'VISITOR' = 'visitors_count.incred',
}

type FormattedEvents = { [K in Event['event_type']]?: Event }

type FormattedData =
  | undefined
  | {
      status?: Status
      events: FormattedEvents
    }

type NewsPublishedEvent = {
  event_type: EventType.NEWS_PUBLISHED
  data: {
    absolute_url: string
    author: User
  }[]
}

type OrderEvent = {
  event_type: EventType.ORDER
  data: {
    user: User
    created_at: string
  }[]
}

type Payload = BackersIncrementEvent | NewsPublishedEvent | OrderEvent | VisitorEventPublish

enum Status {
  NEW = 'new_event',
  SUBSCRIBED = 'subscribed',
}

export type SocketReturn = {
  events: FormattedData
  sendSubscribe: (eventTypes: EventType[]) => void
  sendPublish: (payload: Payload) => void
  socketConnected: boolean
}

type User = {
  name: string
}

type VisitorEvent = {
  event_type: EventType.VISITOR
  data: {
    count: number
  }
}

type VisitorEventPublish = {
  event_type: EventType.VISITOR
}

/**
 * this function return event if they exist
 * with the payload you can subscribe or publish events
 */
export function useSocket(url: string): SocketReturn {
  const [isSocketConnected, setSocketAsConnected] = React.useState(false)
  const socket = React.useRef<Sockette>()

  // Data concated with older data
  const [events, setEvents] = React.useState<FormattedData>()
  // Previous data. Need this because events / data was undefined into useEffect
  // we need previous data to concat new data from new_event with older
  // and remove the older
  const prevEvents = useCommittedRef(events)

  React.useEffect(() => {
    const sockette = new Sockette(url, {
      timeout: 5e3,
      maxAttempts: 1,
      onopen: () => {
        setSocketAsConnected(true)
      },
      onmessage: (e: MessageEvent<any>) => {
        const dataSocket: DataEvents = JSON.parse(e.data)
        const formattedData = dataSocket.events && formatData(dataSocket)
        const concatenatedData = formattedData && concatData(formattedData, prevEvents.current)
        concatenatedData && setEvents(concatenatedData)
      },
      onmaximum: e => console.log('Stop Attempting!', e),
      onreconnect: e => console.log('Reconnecting...', e),
      onerror: e => console.log('Error:', e),
    })

    socket.current = sockette

    return () => {
      socket.current && socket.current.close()
    }
  }, [])

  return { sendSubscribe, sendPublish, events, socketConnected: isSocketConnected }

  function sendSubscribe(eventTypes: EventType[]): void {
    if (socket.current && isSocketConnected) {
      socket.current.send(
        JSON.stringify({
          action: 'subscribe',
          payload: { event_types: eventTypes },
        }),
      )
    }
  }

  function sendPublish(payload: Payload): void {
    if (socket.current && isSocketConnected) {
      socket.current.send(
        JSON.stringify({
          action: 'publish',
          payload,
        }),
      )
    }
  }

  function formatData(dataToFormat: DataEvents): FormattedData {
    const formattedData = dataToFormat.events.reduce((eventsFormatted, currentEvent) => {
      return { ...eventsFormatted, [currentEvent.event_type]: currentEvent }
    }, {})

    return { status: dataToFormat.status, events: formattedData }
  }

  function cleanData(events: FormattedData): FormattedData {
    const cleanEvents = events && { ...events.events }

    cleanEvents &&
      Object.keys(cleanEvents)?.map(cleanEventType => {
        const cleanEvent = cleanEvents[cleanEventType as EventType]

        if (cleanEvent?.event_type === EventType.NEWS_PUBLISHED || cleanEvent?.event_type === EventType.ORDER) {
          if (cleanEvent?.data?.length === 0 || cleanEvent?.data === null) {
            delete cleanEvents[cleanEventType as EventType]
          }
        }

        if (cleanEvent?.event_type === EventType.BACKERS || cleanEvent?.event_type === EventType.VISITOR) {
          if (cleanEvent?.data?.count <= 1) {
            delete cleanEvents[cleanEventType as EventType]
          }
        }
      })

    return { status: events?.status, events: { ...cleanEvents } }
  }

  function concatData(newEvent: FormattedData, oldEvent: FormattedData): FormattedData {
    let concatenatedData
    if (newEvent?.status === Status.SUBSCRIBED) {
      concatenatedData = newEvent
    } else if (newEvent?.status === Status.NEW && oldEvent) {
      concatenatedData = { status: newEvent?.status, events: { ...oldEvent.events, ...newEvent?.events } }
    }

    return cleanData(concatenatedData)
  }
}
